import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, useAnimationControls } from 'framer-motion';
import styles from './MenuHeader.module.css';

interface MenuHeaderProps {
    index?: number;
    name: string;
    subtitle: string;
    onClick: () => void;
    paintOutSignal?: number;
    paintOutExitDelayMs?: number;
    paintOutGeneration?: number;
    onPaintOutComplete?: (generation: number) => void;
    /** Overlay mobile menu: skip hover-driven bar animations (touch-first). */
    disableHover?: boolean;
    /** When false, name/subtitle appear immediately — no intro or paint-out (mobile overlay bar). */
    animated?: boolean;
}

const MenuHeader: React.FC<MenuHeaderProps> = ({
    index = 0,
    name,
    subtitle,
    onClick,
    paintOutSignal = 0,
    paintOutExitDelayMs = 0,
    paintOutGeneration = 0,
    onPaintOutComplete,
    disableHover = false,
    animated = true,
}) => {
    const bgControls = useAnimationControls();
    const introCompleteRef = useRef(false);
    const [textOpacity, setTextOpacity] = useState(() => (animated ? 0 : 1));
    const lastPaintOutSignalRef = useRef(0);

    useEffect(() => {
        if (!animated) {
            introCompleteRef.current = true;
            return;
        }

        let cancelled = false;

        const runIntro = async () => {
            await bgControls.start({
                scaleX: 1,
                transformOrigin: 'left center',
                transition: { duration: 0.3, delay: index * 0.1, ease: 'easeInOut' },
            });
            if (cancelled) return;
            setTextOpacity(1);
            if (cancelled) return;
            await bgControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: 0.3, ease: 'easeInOut' },
            });
            if (!cancelled) introCompleteRef.current = true;
        };

        void runIntro();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- bgControls from useAnimationControls is stable; listing it retriggers intro
    }, [animated, index]);

    useEffect(() => {
        if (!animated) return;
        if (!onPaintOutComplete) return;
        if (paintOutSignal <= 0 || paintOutSignal <= lastPaintOutSignalRef.current) return;
        lastPaintOutSignalRef.current = paintOutSignal;
        let cancelled = false;

        const runPaintOut = async () => {
            introCompleteRef.current = false;
            if (paintOutExitDelayMs > 0) {
                await new Promise<void>((resolve) => setTimeout(resolve, paintOutExitDelayMs));
            }
            if (cancelled) return;

            bgControls.stop();
            setTextOpacity(0);
            await bgControls.start({
                scaleX: 1,
                transformOrigin: 'right center',
                transition: { duration: 0.25, ease: 'easeInOut' },
            });
            if (cancelled) return;
            await bgControls.start({
                scaleX: 0,
                transformOrigin: 'left center',
                transition: { duration: 0.25, ease: 'easeInOut' },
            });
            if (!cancelled && onPaintOutComplete) {
                onPaintOutComplete(paintOutGeneration);
            }
        };

        void runPaintOut();

        return () => {
            cancelled = true;
            bgControls.stop();
        };
    }, [animated, bgControls, onPaintOutComplete, paintOutExitDelayMs, paintOutGeneration, paintOutSignal]);

    const onPointerEnter = useCallback(() => {
        if (disableHover) return;
        if (!introCompleteRef.current) return;
        void bgControls.start({
            scaleX: 1,
            transformOrigin: 'left center',
            transition: { duration: 0.3, ease: 'easeInOut' },
        });
    }, [bgControls, disableHover]);

    const onPointerLeave = useCallback(() => {
        if (disableHover) return;
        if (!introCompleteRef.current) return;
        void (async () => {
            bgControls.stop();
            bgControls.set({
                scaleX: 1,
                transformOrigin: 'left center',
            });
            await bgControls.start({
                scaleX: 0,
                transformOrigin: 'right center',
                transition: { duration: 0.5, ease: 'easeInOut' },
            });
        })();
    }, [bgControls, disableHover]);

    return (
        <div
            className={styles.container}
            onClick={onClick}
            onPointerEnter={disableHover ? undefined : onPointerEnter}
            onPointerLeave={disableHover ? undefined : onPointerLeave}
        >
            <motion.div
                className={styles.background}
                aria-hidden
                initial={{ scaleX: 0 }}
                animate={bgControls}
                style={{ transformOrigin: 'left center' }}
            />
            {animated ? (
                <motion.div
                    className={styles.content}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: textOpacity }}
                    transition={{ duration: 0 }}
                >
                    <div className={styles.name}>{name}</div>
                    <div className={styles.subtitle}>{subtitle}</div>
                </motion.div>
            ) : (
                <div className={styles.content}>
                    <div className={styles.name}>{name}</div>
                    <div className={styles.subtitle}>{subtitle}</div>
                </div>
            )}
        </div>
    );
};

export default MenuHeader;
